import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Attachment } from 'pkg/api/models/attachment';
import type { Dateable } from 'pkg/api/models/dateable';
import type { FormTemplate } from 'pkg/api/models/form';
import type { GameOverview } from 'pkg/api/models/game';
import type { Group } from 'pkg/api/models/group';
import type { EventPracticePlanRef } from 'pkg/api/models/practice_plan';
import type { Record } from 'pkg/api/models/record';
import type { Session } from 'pkg/api/models/session';

export enum EventTypes {
	Camp = 'camp',
	Cup = 'cup',
	Match = 'match',
	Meeting = 'meeting',
	Practice = 'practice',
	Uncategorized = 'uncategorized',
}

export enum EventStatus {
	Canceled = 'canceled',
	Published = 'published',
	Draft = 'draft',
}

export enum EventStates {
	Past = 'past',
	Started = 'started',
	Upcoming = 'upcoming',
}

export enum RSVPInterval {
	RSVPIntervalHour = 'hour',
	RSVPIntervalDay = 'day',
	RSVPIntervalWeek = 'week',
}

export enum EventFlags {
	EventFlagsAutoInviteUsers = 'autoInviteUsers',
	EventFlagsAutoInviteAdminAndStaff = 'autoInviteAdminAndStaff',
	EventFlagsAutoInviteNewUsersAsPending = 'autoInviteNewUsersAsPending',
	EventFlagsAutoInviteNewAdminAndStaffAsPending = 'autoInviteNewAdminAndStaffAsPending',
}

export interface AttendanceStats {
	acceptedCount: number;
	// acceptedParticipantCount excludes organizers and is what capacity is measured against.
	acceptedParticipantCount: number;
	declinedCount: number;
	pendingCount: number;
	attendedCount: number;
	notAttendedCount: number;
	unhandledCount: number;
}

export interface Event extends Record, Dateable, AllowedActionable {
	eventSeriesId: number;
	userId: number;
	startsAt: number;
	endsAt: number;
	startsAtLocal?: string;
	endsAtLocal?: string;
	updatedAt: number;
	deletedAt: number;
	timezone: string;
	status: EventStatus;
	location: string;
	title: string;
	gameId?: number;
	game?: GameOverview;
	groupId: number;
	description: string;
	notes: string;
	isPrivate: boolean;
	isPublic: boolean;
	hideParticipants: boolean;
	physicalStrainDefault: number;
	lokLastReportedDate: number;
	meetBeforeMinutes: number;
	// capacity is the max number of participants. Undefined means uncapped.
	capacity?: number;
	// rsvpIntervalCount is the number of RSVPInterval to require rsvp before. This is in relation to startsAt.
	rsvpIntervalCount: number;
	// rsvpInterval is the time unit of rsvpIntervalCount
	rsvpInterval: RSVPInterval;
	// rsvpBefore is the timestamp that a user must rsvp before
	rsvpBefore: number;
	// inviteIntervalCount is the number of RSVPInterval to send invites before. This is in relation to startsAt.
	inviteIntervalCount: number;
	// inviteInterval is the time unit of inviteIntervalCount
	inviteInterval: RSVPInterval;
	// inviteBefore is the timestamp that invites will be sent before
	inviteBefore: number;
	// reminderIntervalCount is the number of RSVPInterval to send a reminder before. This is in relation to startsAt.
	reminderIntervalCount: number;
	// reminderInterval is the time unit of reminderIntervalCount
	reminderInterval: RSVPInterval;
	flags: EventFlags[];

	// User that has created the event
	user: models.user.User;
	type: EventTypes;
	activities?: models.userActivity.UserActivity[];

	bookings?: models.booking.Booking[];
	users?: models.eventUser.EventUser[];
	eventSeries?: models.eventSeries.EventSeries;
	group?: Group;
	games?: models.game.Game[];
	attachments?: Attachment[];
	sessions?: Session[];

	attendanceStats?: AttendanceStats;

	// capacityReached is a leak-safe full/not-full flag for viewers who can't see the accepted count. Only set on capped events.
	capacityReached?: boolean;

	// fromRegistration says the event is a session of a registration booking. Set for every viewer; carries no names.
	fromRegistration?: boolean;
	// registrationContext names the booking the event came from. Absent while its form is a draft, except for viewers who may open that registration.
	registrationContext?: EventRegistrationContext;

	lokError?: string;

	// The practice plans this practice is in, narrowed to the ones the viewer
	// may open in the planner. Only set on practice events by the show
	// endpoint; absent for players.
	practicePlans?: EventPracticePlanRef[];
	// A plan this practice is in splits the team into session groups. Unlike
	// practicePlans it is set for every viewer — a player's own station hangs
	// on it. Only set on practice events by the show endpoint.
	hasSessionGroups?: boolean;
}

export interface EventRegistrationContext {
	formTitle: string;
	formTemplate: FormTemplate;
	bookingName: string;
	groupId?: number;
	formId?: number;
	bookingId?: number;
	formGuid?: string;
	bookingPublicId?: string;
}
